// src/pages/inventory/InventoryPurchases.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Alert,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    MenuItem,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    List,
    ListItem,
    ListItemText,
    Card,
    CardContent
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ShoppingCart as ShoppingCartIcon,
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Check as CheckIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Pending as PendingIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';

const InventoryPurchases = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [purchases, setPurchases] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [currentPurchase, setCurrentPurchase] = useState({
        supplier_id: '',
        purchase_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        status: 'pending',
        payment_status: 'pending',
        notes: '',
        items: []
    });
    const [currentItem, setCurrentItem] = useState({
        item_id: '',
        quantity: 1,
        unit_price: 0,
        notes: ''
    });

    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            // Fetch purchases
            const purchasesResponse = await fetch('/api/inventory/purchases', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!purchasesResponse.ok) {
                throw new Error('Error al obtener compras');
            }

            const purchasesData = await purchasesResponse.json();
            setPurchases(purchasesData);

            // Fetch suppliers
            const suppliersResponse = await fetch('/api/inventory/suppliers', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!suppliersResponse.ok) {
                throw new Error('Error al obtener proveedores');
            }

            const suppliersData = await suppliersResponse.json();
            setSuppliers(suppliersData);

            // Fetch products
            const productsResponse = await fetch('/api/inventory/items', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!productsResponse.ok) {
                throw new Error('Error al obtener productos');
            }

            const productsData = await productsResponse.json();
            setProducts(productsData);

        } catch (error) {
            console.error('Error fetching data:', error);
            setError(error.message || 'Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleNewPurchase = () => {
        navigate('/inventory/purchases/new');
    };

    const handleViewPurchase = (purchase) => {
        setSelectedPurchase(purchase);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedPurchase(null);
    };

    const handleStatusChange = async (purchaseId, newStatus) => {
        const purchase = purchases.find(p => p.id === purchaseId);
        const hasSerialized = purchase?.items?.some(item => !!item.equipment_type);

        if (hasSerialized && (newStatus === 'received' || newStatus === 'partial')) {
            setError('Esta orden contiene equipos serializados. Para marcarla como recibida o parcial, debe hacerlo desde la pantalla de edición para ingresar sus números de serie (S/N) y MAC.');
            handleCloseDialog();
            navigate(`/inventory/purchases/edit/${purchaseId}`);
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const response = await fetch(`/api/inventory/purchases/${purchaseId}/status`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (!response.ok) {
                throw new Error(`Error al actualizar estado de compra`);
            }

            setSuccess('Estado de compra actualizado exitosamente');
            setTimeout(() => setSuccess(null), 5000);

            fetchData();
        } catch (error) {
            console.error('Error updating purchase status:', error);
            setError(error.message || 'Error al actualizar estado de compra');
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentStatusChange = async (purchaseId, newStatus) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const response = await fetch(`/api/inventory/purchases/${purchaseId}/payment`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (!response.ok) {
                throw new Error(`Error al actualizar estado de pago`);
            }

            setSuccess('Estado de pago actualizado exitosamente');
            setTimeout(() => setSuccess(null), 5000);

            fetchData();
        } catch (error) {
            console.error('Error updating payment status:', error);
            setError(error.message || 'Error al actualizar estado de pago');
        } finally {
            setLoading(false);
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'pending':
                return <Chip label="Pendiente" color="warning" size="small" icon={<PendingIcon />} />;
            case 'partial':
                return <Chip label="Parcial" color="info" size="small" icon={<PendingIcon />} />;
            case 'received':
                return <Chip label="Recibido" color="success" size="small" icon={<CheckCircleIcon />} />;
            case 'cancelled':
                return <Chip label="Cancelado" color="error" size="small" icon={<WarningIcon />} />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    const getPaymentStatusChip = (status) => {
        switch (status) {
            case 'pending':
                return <Chip label="Pendiente" color="warning" size="small" icon={<PendingIcon />} />;
            case 'partial':
                return <Chip label="Parcial" color="info" size="small" icon={<PendingIcon />} />;
            case 'paid':
                return <Chip label="Pagado" color="success" size="small" icon={<CheckCircleIcon />} />;
            case 'cancelled':
                return <Chip label="Cancelado" color="error" size="small" icon={<WarningIcon />} />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    const filteredPurchases = purchases.filter(purchase => {
        let matches = true;

        if (statusFilter && purchase.status !== statusFilter) {
            matches = false;
        }

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const referenceMatch = purchase.reference_number && purchase.reference_number.toLowerCase().includes(searchLower);
            const supplierMatch = purchase.supplier_name && purchase.supplier_name.toLowerCase().includes(searchLower);

            if (!(referenceMatch || supplierMatch)) {
                matches = false;
            }
        }

        return matches;
    });

    if (loading && purchases.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Órdenes de Compra
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        placeholder="Buscar órdenes..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                    />

                    <TextField
                        select
                        label="Estado"
                        size="small"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ minWidth: 150 }}
                        InputProps={{
                            startAdornment: <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="pending">Pendiente</MenuItem>
                        <MenuItem value="partial">Parcial</MenuItem>
                        <MenuItem value="received">Recibido</MenuItem>
                        <MenuItem value="cancelled">Cancelado</MenuItem>
                    </TextField>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleNewPurchase}
                >
                    Nueva Orden de Compra
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Referencia</TableCell>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Proveedor</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="center">Estado</TableCell>
                            <TableCell align="center">Pago</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredPurchases.length > 0 ? (
                            filteredPurchases.map((purchase) => (
                                <TableRow key={purchase.id}>
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            onClick={() => handleViewPurchase(purchase)}
                                            sx={{ 
                                                fontWeight: 'bold',
                                                color: 'primary.main',
                                                cursor: 'pointer',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            {purchase.reference_number || `OC-${purchase.id.toString().padStart(4, '0')}`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {format(new Date(purchase.purchase_date), 'dd/MM/yyyy')}
                                    </TableCell>
                                    <TableCell>{purchase.supplier_name}</TableCell>
                                    <TableCell align="right">
                                        ${parseFloat(purchase.total_amount).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {getStatusChip(purchase.status)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {getPaymentStatusChip(purchase.payment_status)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() => navigate(`/inventory/purchases/edit/${purchase.id}`)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    No hay órdenes de compra disponibles
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog for View Purchase */}
            {selectedPurchase && (
                <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                    <DialogTitle>
                        Detalles de Orden de Compra
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Información General
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Referencia:</strong> {selectedPurchase.reference_number || `OC-${selectedPurchase.id.toString().padStart(4, '0')}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Fecha:</strong> {format(new Date(selectedPurchase.purchase_date), 'dd/MM/yyyy')}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Proveedor:</strong> {selectedPurchase.supplier_name}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Creado por:</strong> {selectedPurchase.user_name}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Estado
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="body2" sx={{ mr: 2 }}>
                                                    <strong>Estado actual:</strong>
                                                </Typography>
                                                {getStatusChip(selectedPurchase.status)}
                                            </Box>
                                            <Box sx={{ mt: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="success"
                                                    onClick={() => handleStatusChange(selectedPurchase.id, 'received')}
                                                    disabled={selectedPurchase.status === 'received'}
                                                    sx={{ mr: 1, mb: 1 }}
                                                >
                                                    Marcar como Recibido
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="warning"
                                                    onClick={() => handleStatusChange(selectedPurchase.id, 'partial')}
                                                    disabled={selectedPurchase.status === 'partial'}
                                                    sx={{ mr: 1, mb: 1 }}
                                                >
                                                    Marcar como Parcial
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleStatusChange(selectedPurchase.id, 'cancelled')}
                                                    disabled={selectedPurchase.status === 'cancelled'}
                                                    sx={{ mb: 1 }}
                                                >
                                                    Cancelar Orden
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Pago
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="body2" sx={{ mr: 2 }}>
                                                    <strong>Estado de pago:</strong>
                                                </Typography>
                                                {getPaymentStatusChip(selectedPurchase.payment_status)}
                                            </Box>
                                            <Typography variant="body2">
                                                <strong>Total:</strong> ${parseFloat(selectedPurchase.total_amount).toFixed(2)}
                                            </Typography>
                                            <Box sx={{ mt: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="success"
                                                    onClick={() => handlePaymentStatusChange(selectedPurchase.id, 'paid')}
                                                    disabled={selectedPurchase.payment_status === 'paid'}
                                                    sx={{ mr: 1, mb: 1 }}
                                                >
                                                    Marcar como Pagado
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="warning"
                                                    onClick={() => handlePaymentStatusChange(selectedPurchase.id, 'partial')}
                                                    disabled={selectedPurchase.payment_status === 'partial'}
                                                    sx={{ mr: 1, mb: 1 }}
                                                >
                                                    Pago Parcial
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handlePaymentStatusChange(selectedPurchase.id, 'cancelled')}
                                                    disabled={selectedPurchase.payment_status === 'cancelled'}
                                                    sx={{ mb: 1 }}
                                                >
                                                    Cancelar Pago
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                        Productos
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Producto</TableCell>
                                                    <TableCell align="center">Cantidad</TableCell>
                                                    <TableCell align="right">Precio Unit.</TableCell>
                                                    <TableCell align="right">Subtotal</TableCell>
                                                    <TableCell align="center">Estado</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedPurchase.items && selectedPurchase.items.length > 0 ? (
                                                    selectedPurchase.items.map((item, index) => (
                                                        <TableRow key={item.id || index}>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                    {item.item_name || 'Producto no especificado'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {item.quantity}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                ${parseFloat(item.unit_price || 0).toFixed(2)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                ${(parseFloat(item.unit_price || 0) * (parseInt(item.quantity) || 0)).toFixed(2)}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {selectedPurchase.status === 'received' ? (
                                                                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 'medium' }}>
                                                                        Recibido ({item.quantity})
                                                                    </Typography>
                                                                ) : (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Pendiente (0/{item.quantity})
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={5} align="center">
                                                            No hay productos registrados en esta orden de compra
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                                {selectedPurchase.notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                            Notas
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2 }}>
                                            <Typography variant="body2">
                                                {selectedPurchase.notes}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>
                            Cerrar
                        </Button>
                        <Button
                            component={Link}
                            to={`/inventory/purchases/edit/${selectedPurchase?.id || ''}`}
                            variant="contained"
                        >
                            Editar
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default InventoryPurchases;